import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface BatchedAvailabilityResult {
  [propertyId: string]: {
    unavailableDates: Date[];
    isLoading: boolean;
    error: string | null;
    refetch?: () => void;
  };
}

export const useBatchedAvailability = (
  propertyIds: string[],
): BatchedAvailabilityResult => {
  const {
    data: availabilityByProperty,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["batched-availability", propertyIds.sort()],
    queryFn: async (): Promise<{ [propertyId: string]: Date[] }> => {
      if (propertyIds.length === 0) return {};

      const { data: blockedDays, error: blockedDaysError } = await supabase
        .from("property_blocked_days")
        .select("property_id, blocked_date")
        .in("property_id", propertyIds)
        .order("blocked_date", { ascending: true });

      if (blockedDaysError) {
        console.error("Error fetching blocked days:", blockedDaysError);
        throw blockedDaysError;
      }

      const { data: confirmedBookings, error: bookingsError } = await supabase
        .from("bookings")
        .select("property_id, check_in, check_out, status")
        .in("property_id", propertyIds)
        .eq("status", "Reservada")
        .order("check_in", { ascending: true });

      if (bookingsError) {
        console.error("Error fetching bookings:", bookingsError);
        throw bookingsError;
      }

      const unavailableDatesByProperty: { [propertyId: string]: Date[] } = {};
      propertyIds.forEach((id) => {
        unavailableDatesByProperty[id] = [];
      });

      blockedDays?.forEach(({ property_id, blocked_date }) => {
        const [year, month, day] = blocked_date.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        unavailableDatesByProperty[property_id].push(date);
      });

      confirmedBookings?.forEach(({ property_id, check_in, check_out }) => {
        const [startYear, startMonth, startDay] = check_in
          .split("-")
          .map(Number);
        const [endYear, endMonth, endDay] = check_out.split("-").map(Number);

        const startDate = new Date(startYear, startMonth - 1, startDay);
        const endDate = new Date(endYear, endMonth - 1, endDay);
        const currentDate = new Date(startDate);

        while (currentDate < endDate) {
          const dateToAdd = new Date(currentDate);
          unavailableDatesByProperty[property_id].push(dateToAdd);

          currentDate.setDate(currentDate.getDate() + 1);
        }
      });

      return unavailableDatesByProperty;
    },
    enabled: propertyIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const formattedResult: BatchedAvailabilityResult = {};
  propertyIds.forEach((propertyId) => {
    formattedResult[propertyId] = {
      unavailableDates: availabilityByProperty?.[propertyId] || [],
      isLoading,
      error: error ? String(error) : null,
      refetch,
    };
  });

  return formattedResult;
};
